import { Field, Label, Switch } from '@headlessui/react'
import React from 'react'

export interface ToggleSwitchProps {
  value?: boolean
  onChange: (newValue: boolean) => void
  disabled?: boolean
  label?: string
  description?: string
  size?: 'small' | 'medium' | 'large'
  color?: 'blue' | 'green' | 'red' | 'gray'
  className?: string
  'aria-label'?: string
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  value = false,
  onChange,
  disabled = false,
  label,
  description,
  size = 'medium',
  color = 'blue',
  className = '',
  'aria-label': ariaLabel,
}) => {
  // 尺寸配置
  const sizeConfig = {
    small: {
      switch: 'h-4 w-7',
      thumb: 'h-3 w-3',
      translateOn: 'translate-x-3',
      translateOff: 'translate-x-0.5',
    },
    medium: {
      switch: 'h-5 w-9',
      thumb: 'h-4 w-4',
      translateOn: 'translate-x-5',
      translateOff: 'translate-x-0.5',
    },
    large: {
      switch: 'h-6 w-11',
      thumb: 'h-5 w-5',
      translateOn: 'translate-x-6',
      translateOff: 'translate-x-0.5',
    },
  }

  // 颜色配置
  const colorConfig = {
    blue: {
      on: 'bg-blue-600',
      off: 'bg-gray-200',
      focus: 'focus:ring-blue-500',
    },
    green: {
      on: 'bg-green-600',
      off: 'bg-gray-200',
      focus: 'focus:ring-green-500',
    },
    red: {
      on: 'bg-red-600',
      off: 'bg-gray-200',
      focus: 'focus:ring-red-500',
    },
    gray: {
      on: 'bg-gray-600',
      off: 'bg-gray-200',
      focus: 'focus:ring-gray-500',
    },
  }

  const sizes = sizeConfig[size]
  const colors = colorConfig[color]

  const switchComponent = (
    <Switch
      checked={value}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel || label}
      className={`
        relative inline-flex ${sizes.switch} items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 ${colors.focus} focus:ring-offset-2
        ${value ? colors.on : colors.off}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      <span
        className={`
          inline-block ${sizes.thumb} transform rounded-full bg-white
          transition-transform duration-200 ease-in-out
          ${value ? sizes.translateOn : sizes.translateOff}
        `}
      />
    </Switch>
  )

  // 如果有标签或描述，使用 Field 包装
  if (label || description) {
    return (
      <Field className="flex items-center justify-between">
        <div className="flex flex-col">
          {label && (
            <Label className="text-sm font-medium text-gray-900 cursor-pointer">
              {label}
            </Label>
          )}
          {description && (
            <span className="text-sm text-gray-500">
              {description}
            </span>
          )}
        </div>
        {switchComponent}
      </Field>
    )
  }

  return switchComponent
}

export default ToggleSwitch